import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Button, Grid, TextField, Alert, CircularProgress,
  Chip, IconButton, Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  useTheme, useMediaQuery,
  Card, CardContent, Pagination, InputAdornment,
} from "@mui/material";
import {
  Add, Edit, Block, CheckCircle, AccountBalanceWallet, Search,
  Refresh, Groups, OpenInNew, Delete,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { GetAllAgents, CreateAgent, UpdateAgent, ToggleAgent } from "api/agent";
import { adminAdjustBalance } from "api/wallet";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import { BORDER, RADIUS_TILE } from "components/bento/bentoTokens";

const CURRENCIES = ["THB", "LAK", "USD"];
const CURRENCY_SYMBOLS = { THB: "฿", LAK: "₭", USD: "$" };

const emptyForm = {
  name: "",
  whatsapp: "",
  description: "",
  shippingFeeRate: [],
};

const BalanceChips = ({ account }) =>
  CURRENCIES.map((cur) =>
    account?.[cur] > 0 ? (
      <Chip
        key={cur}
        label={`${CURRENCY_SYMBOLS[cur]}${(account[cur] || 0).toLocaleString()} ${cur}`}
        color="success"
        size="small"
        sx={{ mr: 0.5, fontWeight: 700 }}
      />
    ) : null
  );

const AgentManagement = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [agents, setAgents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");

  // Wallet adjust dialog
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ amount: "", currency: "THB", adjustType: "add", reason: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await GetAllAgents();
    if (res?.success) {
      setAgents(res.data);
    } else {
      setError(res?.message || "Failed to load agents");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      agents.filter(
        (a) =>
          !q ||
          a.name?.toLowerCase().includes(q) ||
          a.id?.toLowerCase().includes(q) ||
          a.whatsapp?.includes(q)
      )
    );
    setPage(1);
  }, [agents, search]);

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const openCreate = () => {
    setForm(emptyForm);
    setFormError("");
    setEditMode(false);
    setDialogOpen(true);
  };

  const openEdit = (agent) => {
    setForm({
      id: agent.id,
      name: agent.name,
      whatsapp: agent.whatsapp,
      description: agent.description || "",
      shippingFeeRate: agent.shippingFeeRate || [],
    });
    setFormError("");
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editMode && !form.id?.trim()) {
      setFormError(t("agentManagement.agentIdRequired"));
      return;
    }
    if (!form.name?.trim() || !form.whatsapp?.trim()) {
      setFormError(t("agentManagement.nameAndWhatsappRequired"));
      return;
    }
    const parsedRates = (form.shippingFeeRate || []).map((r) => ({
      basePrice: parseFloat(r.basePrice) || 0,
      finalPrice: parseFloat(r.finalPrice) || 0,
    }));
    setSaving(true);
    setFormError("");
    const payload = { ...form, shippingFeeRate: parsedRates };
    const res = editMode ? await UpdateAgent(payload) : await CreateAgent(payload);
    setSaving(false);
    if (res?.success) {
      setSuccess(editMode ? t("agentManagement.agentUpdated") : t("agentManagement.agentCreated"));
      setDialogOpen(false);
      load();
    } else {
      setFormError(res?.message || "Save failed");
    }
  };

  const handleToggle = async (agent) => {
    const action = agent.validflag ? t("agentManagement.deactivate") : t("agentManagement.activate");
    const result = await Swal.fire({
      title: `${action} ${agent.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: action,
    });
    if (!result.isConfirmed) return;
    const res = await ToggleAgent(agent.id);
    if (res?.success) {
      setAgents((prev) =>
        prev.map((a) => a.id === agent.id ? { ...a, validflag: res.data.validflag } : a)
      );
      setSuccess(`${agent.name} ${res.data.validflag ? t("agentManagement.activated") : t("agentManagement.deactivated")}`);
    } else {
      setError(res?.message || "Toggle failed");
    }
  };

  const openAdjust = (agent) => {
    setAdjustTarget(agent);
    setAdjustForm({ amount: "", currency: "THB", adjustType: "add", reason: "" });
    setAdjustDialog(true);
  };

  const handleAdjust = async () => {
    if (!adjustForm.amount || !adjustForm.reason.trim()) {
      setError(t("wallet.admin.amountAndReasonRequired"));
      return;
    }
    if (!adjustTarget?.walletAccount?.id) {
      setError("Agent has no wallet account");
      return;
    }
    setAdjustLoading(true);
    try {
      await adminAdjustBalance(
        adjustTarget.walletAccount.id,
        parseFloat(adjustForm.amount),
        adjustForm.currency,
        adjustForm.adjustType,
        adjustForm.reason.trim()
      );
      setSuccess(t("wallet.admin.balanceAdjustedSuccess", {
        type: adjustForm.adjustType === "add" ? t("wallet.admin.credited") : t("wallet.admin.debited"),
        amount: adjustForm.amount,
        currency: adjustForm.currency,
        txnId: "",
      }));
      setAdjustDialog(false);
      load();
    } catch (err) {
      setError(err?.message || "Adjustment failed");
    } finally {
      setAdjustLoading(false);
    }
  };

  const goToTransactions = (agent) => {
    if (agent.walletAccount?.id) {
      navigate(`/wallet/accounts/${agent.walletAccount.id}/transactions`);
    }
  };

  return (
    <PageShell title={t("agentManagement.title")} subtitle={t("agentManagement.subtitle")}>
      <Grid container spacing={2}>
        {/* Toolbar */}
        <Grid item xs={12}>
          <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1.5} alignItems={{ sm: "center" }} justifyContent="space-between">
            <TextField
              size="small"
              placeholder={t("agentManagement.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 260 }}
            />
            <Box display="flex" gap={1}>
              <Button variant="outlined" color="secondary" size="small" startIcon={<Refresh />} onClick={load} disabled={loading}>
                {t("common.refresh")}
              </Button>
              <Button variant="contained" color="secondary" size="small" startIcon={<Add />} onClick={openCreate}>
                {t("agentManagement.createAgent")}
              </Button>
            </Box>
          </Box>
        </Grid>

        {error && <Grid item xs={12}><Alert severity="error" onClose={() => setError("")}>{error}</Alert></Grid>}
        {success && <Grid item xs={12}><Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert></Grid>}

        <Grid item xs={12}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
          ) : isMobile ? (
            /* ── Mobile: Cards ── */
            <Grid container spacing={2}>
              {paginated.map((agent) => (
                <Grid item xs={12} key={agent._id}>
                  <Card sx={{ borderRadius: RADIUS_TILE, border: `1px solid ${agent.validflag ? BORDER : theme.palette.error.light}` }}>
                    <CardContent sx={{ pb: "12px !important" }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color: "secondary.main" }}>{agent.id}</Typography>
                          <Typography variant="subtitle1" fontWeight={700}>{agent.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{agent.whatsapp}</Typography>
                        </Box>
                        <Chip label={agent.validflag ? t("common.active") : t("common.inactive")} color={agent.validflag ? "success" : "default"} size="small" />
                      </Box>
                      {agent.description && <Typography variant="body2" color="text.secondary" mt={0.5}>{agent.description}</Typography>}
                      <Box mt={1}>
                        {agent.walletAccount ? (
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            <BalanceChips account={agent.walletAccount} />
                            {!CURRENCIES.some((c) => agent.walletAccount?.[c] > 0) && (
                              <Typography variant="caption" color="text.secondary">{t("agentManagement.zeroBalance")}</Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">{t("agentManagement.noWallet")}</Typography>
                        )}
                      </Box>
                      <Box display="flex" gap={1} mt={1.5} flexWrap="wrap">
                        <Button variant="outlined" color="secondary" size="small" startIcon={<Edit />} onClick={() => openEdit(agent)}>{t("common.edit")}</Button>
                        {agent.walletAccount && (
                          <>
                            <Button variant="outlined" color="secondary" size="small" startIcon={<AccountBalanceWallet />} onClick={() => openAdjust(agent)}>{t("wallet.admin.adjust")}</Button>
                            <Button variant="outlined" color="secondary" size="small" startIcon={<OpenInNew />} onClick={() => goToTransactions(agent)}>{t("wallet.admin.transactions")}</Button>
                          </>
                        )}
                        <Button
                          variant="outlined"
                          color={agent.validflag ? "error" : "success"}
                          size="small"
                          startIcon={agent.validflag ? <Block /> : <CheckCircle />}
                          onClick={() => handleToggle(agent)}
                        >
                          {agent.validflag ? t("agentManagement.deactivate") : t("agentManagement.activate")}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            /* ── Desktop: Table ── */
            <BentoCard bodySx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("agentManagement.agentId")}</TableCell>
                      <TableCell>{t("agentManagement.name")}</TableCell>
                      <TableCell>{t("agentManagement.whatsapp")}</TableCell>
                      <TableCell>{t("agentManagement.wallet")}</TableCell>
                      <TableCell>{t("agentManagement.status")}</TableCell>
                      <TableCell>{t("common.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.map((agent) => (
                      <TableRow key={agent._id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color: "secondary.main" }}>{agent.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{agent.name}</Typography>
                          {agent.description && <Typography variant="caption" color="text.secondary">{agent.description}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{agent.whatsapp}</Typography>
                        </TableCell>
                        <TableCell>
                          {agent.walletAccount ? (
                            <Box>
                              {CURRENCIES.map((cur) => (
                                <Typography key={cur} variant="caption" display="block" fontWeight={agent.walletAccount[cur] > 0 ? 700 : 400} color={agent.walletAccount[cur] > 0 ? "success.main" : "text.secondary"}>
                                  {CURRENCY_SYMBOLS[cur]}{(agent.walletAccount[cur] || 0).toLocaleString()} {cur}
                                </Typography>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">{t("agentManagement.noWallet")}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={agent.validflag ? t("common.active") : t("common.inactive")} color={agent.validflag ? "success" : "default"} size="small" />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title={t("common.edit")}>
                              <IconButton size="small" color="secondary" onClick={() => openEdit(agent)}><Edit fontSize="small" /></IconButton>
                            </Tooltip>
                            {agent.walletAccount && (
                              <>
                                <Tooltip title={t("wallet.admin.adjustBalance")}>
                                  <IconButton size="small" color="secondary" onClick={() => openAdjust(agent)}><AccountBalanceWallet fontSize="small" /></IconButton>
                                </Tooltip>
                                <Tooltip title={t("wallet.admin.viewTransactions")}>
                                  <IconButton size="small" color="secondary" onClick={() => goToTransactions(agent)}><OpenInNew fontSize="small" /></IconButton>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title={agent.validflag ? t("agentManagement.deactivate") : t("agentManagement.activate")}>
                              <IconButton size="small" color={agent.validflag ? "error" : "success"} onClick={() => handleToggle(agent)}>
                                {agent.validflag ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" p={2}>
                  <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="secondary" />
                </Box>
              )}
            </BentoCard>
          )}
          {isMobile && totalPages > 1 && (
            <Box display="flex" justifyContent="center" pt={2}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="secondary" size="small" />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Groups />
          {editMode ? t("agentManagement.editAgent") : t("agentManagement.createAgent")}
        </DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} mt={0.5}>
            {!editMode && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth label={t("agentManagement.agentId")}
                  value={form.id || ""}
                  onChange={(e) => setForm({ ...form, id: e.target.value.toUpperCase() })}
                  placeholder="e.g. DKC"
                  helperText={t("agentManagement.agentIdHelper")}
                  inputProps={{ style: { fontFamily: "monospace", textTransform: "uppercase" } }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={editMode ? 6 : 8}>
              <TextField
                fullWidth label={t("agentManagement.name")} required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            {editMode && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t("agentManagement.agentId")}
                  value={form.id || ""}
                  disabled
                  inputProps={{ style: { fontFamily: "monospace" } }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={editMode ? 12 : 12}>
              <TextField
                fullWidth label={t("agentManagement.whatsapp")} required
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                disabled={editMode}
                helperText={editMode ? t("agentManagement.whatsappCannotChange") : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label={t("agentManagement.description")} multiline rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Grid>

            {/* ── Shipping Fee Rate ── */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2">{t("agentManagement.shippingFeeRate")}</Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<Add />}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      shippingFeeRate: [...(f.shippingFeeRate || []), { basePrice: "", finalPrice: "" }],
                    }))
                  }
                >
                  {t("agentManagement.addRate")}
                </Button>
              </Box>
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto", borderRadius: RADIUS_TILE, borderColor: BORDER }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("agentManagement.basePrice")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("agentManagement.finalPrice")}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(form.shippingFeeRate || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="caption" color="text.disabled">{t("agentManagement.noRates")}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (form.shippingFeeRate || []).map((rate, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ color: "text.secondary", width: 36 }}>{idx + 1}</TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0, step: 1 }}
                              value={rate.basePrice}
                              onChange={(e) => {
                                const updated = [...form.shippingFeeRate];
                                updated[idx] = { ...updated[idx], basePrice: e.target.value };
                                setForm((f) => ({ ...f, shippingFeeRate: updated }));
                              }}
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0, step: 1 }}
                              value={rate.finalPrice}
                              onChange={(e) => {
                                const updated = [...form.shippingFeeRate];
                                updated[idx] = { ...updated[idx], finalPrice: e.target.value };
                                setForm((f) => ({ ...f, shippingFeeRate: updated }));
                              }}
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, width: 40 }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  shippingFeeRate: f.shippingFeeRate.filter((_, i) => i !== idx),
                                }))
                              }
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setDialogOpen(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : undefined}
          >
            {saving ? t("common.loading") : t("common.submit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Wallet Adjust Dialog ── */}
      <Dialog open={adjustDialog} onClose={() => !adjustLoading && setAdjustDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccountBalanceWallet />
          {t("wallet.admin.adjustBalance")}
        </DialogTitle>
        <DialogContent>
          {adjustTarget && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {adjustTarget.name} ({adjustTarget.id})
            </Typography>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>{t("wallet.admin.authorizedAdjustmentWarning")}</Alert>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth label={t("wallet.amount")} type="number" inputProps={{ min: 0.01, step: 0.01 }}
                value={adjustForm.amount}
                onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select fullWidth label={t("wallet.currency")}
                value={adjustForm.currency}
                onChange={(e) => setAdjustForm({ ...adjustForm, currency: e.target.value })}
                SelectProps={{ native: true }}
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select fullWidth label={t("wallet.admin.adjustType")}
                value={adjustForm.adjustType}
                onChange={(e) => setAdjustForm({ ...adjustForm, adjustType: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="add">{t("wallet.admin.add")}</option>
                <option value="deduct">{t("wallet.admin.deduct")}</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label={t("wallet.admin.reason")}
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setAdjustDialog(false)} disabled={adjustLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color={adjustForm.adjustType === "add" ? "success" : "error"}
            onClick={handleAdjust}
            disabled={adjustLoading || !adjustForm.amount || !adjustForm.reason}
            startIcon={adjustLoading ? <CircularProgress size={18} /> : undefined}
          >
            {adjustLoading ? t("wallet.admin.processing") : `${adjustForm.adjustType === "add" ? "Credit" : "Debit"} ${adjustForm.currency}`}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default AgentManagement;
